import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { TOKEN } from "../../helper/constant";
/* Imported lazily below, not here: refreshManager imports the store, and the
   store imports this slice. A static import closes that ring, and whichever
   module enters it first leaves store.js reading `apiSlice.reducerPath`
   before this const exists ("Cannot access 'apiSlice' before initialization"
   in the production bundle, blank page). */

const rawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BASE_API_URL,
  prepareHeaders: (headers, { getState }) => {
    /* Redux is the live copy (a refresh updates it first); storage is the
       fallback for the legacy path, which wrote the JWT there before the
       profile call that fills redux. Read at request time, never cached. */
    const token =
      getState()?.auth?.accessToken || localStorage.getItem(TOKEN) || "";
    if (token) headers.set("Authorization", `Bearer ${token}`);
    headers.set("Accept", "application/json");
    headers.set("Cache-Control", "no-cache");
    headers.set("Pragma", "no-cache");
    headers.set("Expires", "0");
    return headers;
  },
});

/* Same 401 -> refresh -> replay-once policy as the axios instance. A refresh
   that fails (session over, or network) leaves the original 401 for the
   caller; the refresh manager has already raised the modal if the session
   is gone. Non-401 errors and fetch failures pass straight through. */
const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);
  if (result?.error?.status !== 401) return result;

  const { refreshAccessToken } = await import(
    /* webpackMode: "eager" */ "../auth/refreshManager"
  );
  const refreshed = await refreshAccessToken();
  if (!refreshed) return result;

  return rawBaseQuery(args, api, extraOptions);
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,

  tagTypes: [
    "Category",
    "Author",
    "Post",
    "Cbt",
    "KybVerification",
    "ContentCreators",
    "PendingJobs",
    "Library",
    "CbtRun",
    "Partner",
  ],

  endpoints: (builder) => ({
    /* ---------------- Role library (category -> role -> specialty -> tool, role -> skill) ---------------- */
    getLibraryKinds: builder.query({
      query: () => "api/v1/admin/library/kinds",
      transformResponse: (response) => response.data,
    }),
    getLibraryCounts: builder.query({
      query: () => "api/v1/admin/library/counts",
      transformResponse: (response) => response.data,
      providesTags: ["Library"],
    }),
    getLibrary: builder.query({
      query: ({
        kind,
        parentId,
        unassigned,
        generic,
        status,
        search,
        page = 1,
        limit = 50,
      }) => {
        const qs = new URLSearchParams();
        if (parentId) qs.set("parentId", parentId);
        if (unassigned) qs.set("unassigned", "1");
        if (generic) qs.set("generic", "1");
        if (status) qs.set("status", status);
        if (search) qs.set("search", search);
        qs.set("page", page);
        qs.set("limit", limit);
        return `api/v1/admin/library/${kind}?${qs.toString()}`;
      },
      providesTags: (result, error, arg) => [
        { type: "Library", id: arg.kind },
        "Library",
      ],
    }),
    getLibraryOptions: builder.query({
      query: ({ kind, parentId }) =>
        `api/v1/admin/library/${kind}/options${
          parentId ? `?parentId=${parentId}` : ""
        }`,
      transformResponse: (response) => response.data,
      providesTags: ["Library"],
    }),
    getLibraryDescendants: builder.query({
      query: ({ kind, id }) => `api/v1/admin/library/${kind}/${id}/descendants`,
      transformResponse: (response) => response.data,
      providesTags: ["Library"],
    }),
    createLibraryItem: builder.mutation({
      query: ({ kind, body }) => ({
        url: `api/v1/admin/library/${kind}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Library", "Cbt"],
    }),
    updateLibraryItem: builder.mutation({
      query: ({ kind, id, body }) => ({
        url: `api/v1/admin/library/${kind}/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Library", "Cbt"],
    }),
    approveLibraryItem: builder.mutation({
      query: ({ kind, id }) => ({
        url: `api/v1/admin/library/${kind}/${id}/approve`,
        method: "POST",
      }),
      invalidatesTags: ["Library", "Cbt", "HireRequest"],
    }),
    rejectLibraryItem: builder.mutation({
      query: ({ kind, id }) => ({
        url: `api/v1/admin/library/${kind}/${id}/reject`,
        method: "POST",
      }),
      invalidatesTags: ["Library", "Cbt"],
    }),
    /* Multipart: { file, dryRun }. fetchBaseQuery leaves Content-Type alone
       for a FormData body, so the boundary is set by the browser. */
    bulkImportLibrary: builder.mutation({
      query: ({ file, dryRun, resolutions }) => {
        const form = new FormData();
        form.append("file", file);
        form.append("dryRun", dryRun ? "1" : "0");
        if (resolutions && Object.keys(resolutions).length)
          form.append("resolutions", JSON.stringify(resolutions));
        return { url: "api/v1/admin/library/bulk", method: "POST", body: form };
      },
      invalidatesTags: (result, error, arg) =>
        arg.dryRun ? [] : ["Library", "Cbt"],
    }),
    /* The import job a bulk upload creates. dryRun=0 answers 202 with an id
       instead of doing the work inline, so these two are how the page finds
       out what happened. The status poll is a few hundred bytes; the report
       is a megabyte and is fetched only when someone opens it. */
    getLibraryImport: builder.query({
      query: (id) => ({ url: `api/v1/admin/library/imports/${id}` }),
    }),
    getLibraryImportReport: builder.query({
      query: (id) => ({ url: `api/v1/admin/library/imports/${id}/report` }),
    }),

    /* Same shape as bulkImportLibrary, one column of names to delete. Only
       `kind: 'skill'` is accepted by the server for now. */
    bulkDeleteLibrary: builder.mutation({
      query: ({ file, kind = "skill", dryRun, resolutions }) => {
        const form = new FormData();
        form.append("file", file);
        form.append("kind", kind);
        form.append("dryRun", dryRun ? "1" : "0");
        if (resolutions && Object.keys(resolutions).length)
          form.append("resolutions", JSON.stringify(resolutions));
        return {
          url: "api/v1/admin/library/bulk-delete",
          method: "POST",
          body: form,
        };
      },
      invalidatesTags: (result, error, arg) =>
        arg.dryRun ? [] : ["Library", "Cbt"],
    }),
    deleteLibraryItem: builder.mutation({
      query: ({ kind, id, parentId }) => ({
        url: `api/v1/admin/library/${kind}/${id}${
          parentId ? `?parentId=${parentId}` : ""
        }`,
        method: "DELETE",
      }),
      invalidatesTags: ["Library", "Cbt"],
    }),

    /* ---------------- AI CBT question generator (runs and their drafts) ---------------- */
    // Every change to a run or a draft invalidates 'CbtRun', so the list and
    // the open run page both reload with what the server now holds.
    getCbtRuns: builder.query({
      query: ({ status, page = 1, limit = 20 }) => {
        const qs = new URLSearchParams();
        if (status) qs.set("status", status);
        qs.set("page", page);
        qs.set("limit", limit);
        return `api/v1/admin/cbt-question-runs?${qs.toString()}`;
      },
      transformResponse: (response) => response.data,
      providesTags: ["CbtRun"],
    }),
    getCbtRun: builder.query({
      query: (id) => `api/v1/admin/cbt-question-runs/${id}`,
      transformResponse: (response) => response.data,
      providesTags: ["CbtRun"],
    }),
    getCbtUsage: builder.query({
      query: () => "api/v1/admin/cbt-question-runs/usage",
      transformResponse: (response) => response.data,
      providesTags: ["CbtRun"],
    }),
    getCbtStartHere: builder.query({
      query: () => "api/v1/admin/cbt-question-runs/start-here",
      transformResponse: (response) => response.data,
      // A new run changes the counts, so the list reloads after every run action
      providesTags: ["CbtRun"],
    }),
    createCbtRun: builder.mutation({
      query: (body) => ({
        url: "api/v1/admin/cbt-question-runs",
        method: "POST",
        body,
      }),
      invalidatesTags: ["CbtRun"],
    }),
    suggestCbtTopics: builder.mutation({
      query: (body) => ({
        url: "api/v1/admin/cbt-question-runs/suggest-topics",
        method: "POST",
        body,
      }),
      // A suggestion spends Claude money, so the usage meters reload
      invalidatesTags: ["CbtRun"],
    }),

    checkCbtRun: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/cbt-question-runs/${id}/check`,
        method: "POST",
      }),
      invalidatesTags: ["CbtRun"],
    }),
    submitCbtRun: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/cbt-question-runs/${id}/submit`,
        method: "POST",
      }),
      invalidatesTags: ["CbtRun"],
    }),
    sendBackCbtRun: builder.mutation({
      query: ({ id, note }) => ({
        url: `api/v1/admin/cbt-question-runs/${id}/send-back`,
        method: "POST",
        body: { note },
      }),
      invalidatesTags: ["CbtRun"],
    }),
    approveCbtRun: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/cbt-question-runs/${id}/approve`,
        method: "POST",
      }),
      invalidatesTags: ["CbtRun"],
    }),
    retryCbtRun: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/cbt-question-runs/${id}/retry`,
        method: "POST",
      }),
      invalidatesTags: ["CbtRun"],
    }),
    cancelCbtRun: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/cbt-question-runs/${id}/cancel`,
        method: "POST",
      }),
      invalidatesTags: ["CbtRun"],
    }),
    editCbtDraft: builder.mutation({
      query: ({ id, body }) => ({
        url: `api/v1/admin/cbt-question-drafts/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["CbtRun"],
    }),
    deleteCbtDraft: builder.mutation({
      query: ({ id, reason }) => ({
        url: `api/v1/admin/cbt-question-drafts/${id}/delete`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: ["CbtRun"],
    }),
    restoreCbtDraft: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/cbt-question-drafts/${id}/restore`,
        method: "POST",
      }),
      invalidatesTags: ["CbtRun"],
    }),
    confirmCbtDraft: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/cbt-question-drafts/${id}/confirm`,
        method: "POST",
      }),
      invalidatesTags: ["CbtRun"],
    }),

    recheckCbtDraft: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/cbt-question-drafts/${id}/recheck`,
        method: "POST",
      }),
      invalidatesTags: ["CbtRun"],
    }),
    rewriteCbtDraft: builder.mutation({
      query: ({ id, reason }) => ({
        url: `api/v1/admin/cbt-question-drafts/${id}/rewrite`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: ["CbtRun"],
    }),

    getAllContentCreators: builder.query({
      query: (page) =>
        `api/v1/content-creator/getAllContentCreator?page=${page + 1}`,
      providesTags: ["ContentCreators"],
    }),

    registerCreator: builder.mutation({
      query: (data) => ({
        url: "api/v1/content-creator/register",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ContentCreators"],
    }),

    /* Bulk talent import.
       Multipart: fetchBaseQuery leaves Content-Type alone for a FormData
       body, so the browser sets the boundary. */

    /* One talent from the form. Still writes a batch of one, so a single
       registration has the same audit trail as a sheet, and is processed
       inline because the admin is watching. */
    registerTalent: builder.mutation({
      query: (data) => ({
        url: "api/v1/admin/talent-imports/single",
        method: "POST",
        body: data,
      }),
    }),

    /* Only for an admin-created account nobody has set a password on;
       the server refuses otherwise. */
    deleteTalent: builder.mutation({
      query: (userId) => ({
        url: `api/v1/admin/talents/${userId}`,
        method: 'DELETE',
      }),
    }),

    /* The set-password invite again. A fresh token each time, so the old
       link stops working rather than two live links in one inbox. */
    resendTalentInvite: builder.mutation({
      query: ({ userId, subject, message, ctaLabel }) => ({
        url: `api/v1/admin/talents/${userId}/resend-invite`,
        method: "POST",
        body: {
          subject: subject,
          message: message,
          cta_label: ctaLabel,
        },
      }),
    }),

    /* Writes nothing — every row comes back with a verdict so the admin
       sees who would be created, who would be skipped and why. */
    previewTalentImport: builder.mutation({
      query: ({ file }) => {
        const form = new FormData();
        form.append("file", file);
        return {
          url: "api/v1/admin/talent-imports/preview",
          method: "POST",
          body: form,
        };
      },
    }),

    /* Confirmed. Answers 202 with an import id; the runner does the work,
       so the page polls rather than waiting on this request. */
    createTalentImport: builder.mutation({
      query: ({ file, subject, message, partnerId, ctaLabel }) => {
        const form = new FormData();
        form.append("file", file);
        form.append("subject", subject);
        form.append("message", message);
        if (partnerId) form.append("partner_id", partnerId);
        if (ctaLabel) form.append("cta_label", ctaLabel);
        return {
          url: "api/v1/admin/talent-imports",
          method: "POST",
          body: form,
        };
      },
    }),

    /* A few hundred bytes, polled every two seconds. */
    getTalentImport: builder.query({
      query: (id) => ({ url: `api/v1/admin/talent-imports/${id}` }),
    }),

    /* The per-row outcomes, paged and filterable by status. This is the
       answer to "which ones didn't work", so it is a real endpoint rather
       than a blob on the batch. */
    getTalentImportRows: builder.query({
      query: ({ id, status, page = 1, perPage = 50 }) => {
        const params = new URLSearchParams({
          page: String(page),
          per_page: String(perPage),
        });
        if (status) params.set("status", status);
        return {
          url: `api/v1/admin/talent-imports/${id}/rows?${params.toString()}`,
        };
      },
    }),

    /* Only send_failed rows are requeued — the account already exists, so
       trying again duplicates nobody. */
    retryTalentImport: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/talent-imports/${id}/retry`,
        method: "POST",
      }),
    }),

    getCategory: builder.query({
      query: () => "api/v1/admin/getBlogCats",
      transformResponse: (response) => response.data,
      providesTags: ["Category"],
    }),

    addNewCategory: builder.mutation({
      query: (initialPost) => ({
        url: "api/v1/admin/createBlogCat",
        body: initialPost,
        method: "POST",
        // Include the entire post object as the body of the request
      }),
      invalidatesTags: ["Category"],
    }),

    deleteCategory: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/deleteBlogCat/${id}`,
        method: "DELETE",
        responseHandler: (response) => response.text(),
      }),
      invalidatesTags: ["Category"],
    }),

    getAuthor: builder.query({
      query: () => "api/v1/admin/getBlogAuthors",
      transformResponse: (response) => response.data,
      providesTags: ["Author"],
    }),

    addNewAuthor: builder.mutation({
      query: (initialName) => ({
        url: "api/v1/admin/createBlogAuthor",
        method: "POST",
        body: initialName,
        headers: {
          "Content-Type": "application/json",
        },
        // Include the entire post object as the body of the request
      }),
      // transformResponse : (response, meta, arg ) => response.data,
      invalidatesTags: ["Author"],
    }),

    deleteAuthor: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/deleteBlogAuthor/${id}`,
        method: "DELETE",
        // responseHandler: (response) => response.text()
      }),
      // invalidatesTags: (result, error, arg) => [{ type: 'Author', id: arg.id }],
      invalidatesTags: ["Author"],
    }),

    blogPost: builder.mutation({
      query: (initialBlog) => ({
        url: "api/v1/admin/createBlogPost",
        method: "PUT",
        body: initialBlog,
        headers: {
          Accept: "*/*",
          "Access-Control-Allow-Headers":
            "origin,X-Requested-With,content-type,accept",
        },
        // Include the entire post object as the body of the request
      }),
      // transformResponse : (response, meta, arg ) => response.data,
      invalidatesTags: ["Post"],
    }),

    editPost: builder.mutation({
      query: ({ id, ...rest }) => ({
        url: `api/v1/admin/createBlogPost/${id}`,
        method: "PATCH",
        body: rest,
        headers: {
          Accept: "*/*",
          "Access-Control-Allow-Headers":
            "origin,X-Requested-With,content-type,accept",
        },
      }),
      // invalidatesTags: (result, error, arg) => [{ type: 'Post', id: arg.id }],
      invalidatesTags: ["Post"],
    }),

    getBlogPost: builder.query({
      query: () => "api/v1/admin/getBlogPosts",
      transformResponse: (response) => response.data.data,
      // providesTags: (result = [], error, arg) => [
      //   'Post',
      //   ...result.map(({ id }) => ({ type: 'Post', id }))
      // ]
      providesTags: ["Post"],
    }),

    getPost: builder.query({
      query: (postId) => `/api/v1/admin/getBlogPosts/${postId}`,
      transformResponse: (response) => response.data.data,
      // providesTags: (result, error, id) => [{ type: 'Post', id }],
      providesTags: ["Post"],
    }),

    deleteBlogPost: builder.mutation({
      query: (postId) => ({
        url: `api/v1/admin/deleteBlogPost/${postId}`,
        method: "DELETE",
        // responseHandler: (response) => response.text()
      }),
      // invalidatesTags: (result, error, arg) => [{ type: 'Post', id: arg.id }],
      // invalidatesTags: (result, error, id) => [{ type: 'Post', id }],
      invalidatesTags: ["Post"],
    }),

    verifySoftSkillEmail: builder.mutation({
      query: (email) => ({
        url: `api/v1/admin/verifySoftSkillEmail`,
        method: "PUT",
        body: email,
      }),
      transformResponse: (response) => response.data,
    }),

    getPlatformKpi: builder.query({
      query: () => "api/v1/admin/getPlatformKPI",
      transformResponse: (response) => response.data,
      providesTags: ["PlatformKpi"],
    }),

    addDevsSoftSkillResult: builder.mutation({
      query: (data) => ({
        url: "api/v1/admin/addDevsSoftSkillResult",
        method: "POST",
        body: data,
        headers: {
          Accept: "*/*",
          "Access-Control-Allow-Headers":
            "origin,X-Requested-With,content-type,accept",
        },
      }),
    }),

    getAllHireRequest: builder.query({
      query: (page) => `api/v1/admin/getAllHireRequest?page=${page + 1}`,
      transformResponse: (response) => response.data,
      providesTags: ["HireRequest"],
    }),

    getAHireRequestById: builder.query({
      query: (reqId) => `api/v1/admin/getAHireRequestById/${reqId}`,
      transformResponse: (response) => response.data,
      providesTags: ["HireRequest"],
    }),

    getAllMatchedTalentPerJobReq: builder.query({
      // ring: "inside" (default, the match) or a wider tab key from getLocationTabs.
      query: ({ reqId, page, ring }) =>
        `/api/v1/admin/getAdminMatchedTalents/${reqId}?page=${page + 1}${
          ring && ring !== "inside" ? `&ring=${ring}` : ""
        }`,
      transformResponse: (response) => response.data,
      providesTags: ["HireRequest"],
    }),

    getLocationTabs: builder.query({
      query: (reqId) => `/api/v1/admin/getLocationTabs/${reqId}`,
      transformResponse: (response) => response.data,
      providesTags: ["HireRequest"],
    }),

    rematchTalentsMutation: builder.mutation({
      query: (reqId) => ({
        url: `/api/v1/admin/rematch/${reqId}`,
        method: "PUT",
        headers: {
          Accept: "*/*",
          "Access-Control-Allow-Headers":
            "origin,X-Requested-With,content-type,accept",
        },
      }),
      invalidatesTags: ["HireRequest"],
    }),

    getACbtDetail: builder.query({
      query: (id) => `/api/v1/admin/getATalentsCbtDetails/${id}`,
      transformResponse: (response) => response.data,
      // providesTags: (result, error, id) => [{ type: 'Post', id }],
      providesTags: ["Post"],
    }),
    getRealCbtNames: builder.query({
      query: (id) => `/api/v1/admin/getATalentsCbtDetails/${id}`,
      transformResponse: (response) => response.data,
      // providesTags: (result, error, id) => [{ type: 'Post', id }],
      providesTags: ["Post"],
    }),
    getTalentWithIncompleteProfile: builder.query({
      query: ({ page, limit }) =>
        `api/v1/admin/getTalentWithIncompleteProfile?page=${page}&limit=${limit}`,
      transformResponse: (response) => ({
        talents: response.data,
        total: response.pagination.total,
        perPage: response.pagination.perPage,
        currentPage: response.pagination.page,
        lastPage: response.pagination.lastPage,
      }),
      providesTags: ["IncompleteProfiles"],
    }),
    getAllKybVerifications: builder.query({
      query: (page) =>
        `api/v1/admin/kybVerifications?page=${page + 1}&limit=10`,
      transformResponse: (response) => response.data,
      providesTags: ["KybVerification"],
    }),
    getKybVerificationById: builder.query({
      query: (id) => `api/v1/admin/kybVerifications/${id}`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, id) => [{ type: "KybVerification", id }],
    }),
    approveKybVerification: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/kybVerifications/${id}/approve`,
        method: "PUT",
      }),
      invalidatesTags: ["KybVerification"],
    }),
    rejectKybVerification: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `api/v1/admin/kybVerifications/${id}/reject`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["KybVerification"],
    }),
    updateKybDocStatus: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `api/v1/admin/kybVerifications/${id}/update-doc`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["KybVerification"],
    }),

    /* ---------------- Partners (droomworkApi Admin/PartnerController) ---------------- */
    /* page is 1-based. status: "all" | "pending" | "verified" | "suspended".
       data = { total, perPage, page, lastPage, data: rows, counts }. */
    getPartners: builder.query({
      query: ({ page = 1, limit = 10, search, status }) => {
        const qs = new URLSearchParams();
        qs.set("page", page);
        qs.set("limit", limit);
        if (search) qs.set("search", search);
        if (status && status !== "all") qs.set("status", status);
        return `api/v1/admin/partners?${qs}`;
      },
      transformResponse: (response) => response.data,
      providesTags: ["Partner"],
    }),
    getPartnerById: builder.query({
      query: (id) => `api/v1/admin/partners/${id}`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, id) => [{ type: "Partner", id }],
    }),
    /* The three actions answer with the updated partner; invalidating the
       tag refreshes both the list and the detail page. */
    verifyPartner: builder.mutation({
      query: (id) => ({ url: `api/v1/admin/partners/${id}/verify`, method: "PUT" }),
      invalidatesTags: ["Partner"],
    }),
    suspendPartner: builder.mutation({
      query: ({ id, reason }) => ({
        url: `api/v1/admin/partners/${id}/suspend`,
        method: "PUT",
        body: { reason },
      }),
      invalidatesTags: ["Partner"],
    }),
    reinstatePartner: builder.mutation({
      query: (id) => ({ url: `api/v1/admin/partners/${id}/reinstate`, method: "PUT" }),
      invalidatesTags: ["Partner"],
    }),
    /* A verified partner's request to change their name. */
    approvePartnerName: builder.mutation({
      query: (id) => ({ url: `api/v1/admin/partners/${id}/name-request/approve`, method: "PUT" }),
      invalidatesTags: ["Partner"],
    }),
    rejectPartnerName: builder.mutation({
      query: ({ id, reason }) => ({
        url: `api/v1/admin/partners/${id}/name-request/reject`,
        method: "PUT",
        body: { reason },
      }),
      invalidatesTags: ["Partner"],
    }),
    closeHireRequest: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/closeHireRequest/${id}`,
        method: "PUT",
      }),
      invalidatesTags: ["HireRequest"],
    }),

    /* getPendingRoles / approveRoleBundle / rejectRoleBundle were replaced by
       the library endpoints above; the Role Library page approves per row. */

    getAllPendingJobs: builder.query({
      query: (page) => `api/v1/admin/jobs/pending?page=${page + 1}`,
      transformResponse: (response) => response.data,
      providesTags: ["PendingJobs"],
    }),

    approveJob: builder.mutation({
      query: (id) => ({
        url: `api/v1/admin/jobs/${id}/approve`,
        method: "PUT",
      }),
      invalidatesTags: ["PendingJobs", "HireRequest"],
    }),

    rejectJob: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `api/v1/admin/jobs/${id}/reject`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["PendingJobs", "HireRequest"],
    }),

    editJob: builder.mutation({
      query: ({ id, data }) => ({
        url: `api/v1/admin/jobs/${id}/edit`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["PendingJobs", "HireRequest"],
    }),
  }),
});

export const {
  useGetCategoryQuery,
  useAddNewCategoryMutation,
  useBlogPostMutation,
  useDeleteCategoryMutation,
  useGetAuthorQuery,
  useAddNewAuthorMutation,
  useDeleteAuthorMutation,
  useGetBlogPostQuery,
  useGetPostQuery,
  useDeleteBlogPostMutation,
  useEditPostMutation,
  useVerifySoftSkillEmailMutation,
  useAddDevsSoftSkillResultMutation,
  useGetPlatformKpiQuery,
  useGetAllHireRequestQuery,
  useGetAHireRequestByIdQuery,
  useLazyGetAHireRequestByIdQuery,
  useGetAllMatchedTalentPerJobReqQuery,
  useGetLocationTabsQuery,
  useRematchTalentsMutation,
  useGetACbtDetailQuery,
  useGetTalentWithIncompleteProfileQuery,
  useCloseHireRequestMutation,
  useGetLibraryKindsQuery,
  useGetLibraryCountsQuery,
  useGetLibraryQuery,
  useGetLibraryOptionsQuery,
  useGetLibraryDescendantsQuery,
  useCreateLibraryItemMutation,
  useUpdateLibraryItemMutation,
  useApproveLibraryItemMutation,
  useRejectLibraryItemMutation,
  useDeleteLibraryItemMutation,
  useBulkImportLibraryMutation,
  useLazyGetLibraryImportQuery,
  useLazyGetLibraryImportReportQuery,
  useBulkDeleteLibraryMutation,
  useRegisterTalentMutation,
  useResendTalentInviteMutation,
  useDeleteTalentMutation,
  usePreviewTalentImportMutation,
  useCreateTalentImportMutation,
  useLazyGetTalentImportQuery,
  useLazyGetTalentImportRowsQuery,
  useRetryTalentImportMutation,
  useGetCbtRunsQuery,
  useGetCbtRunQuery,
  useGetCbtUsageQuery,
  useGetCbtStartHereQuery,
  useCreateCbtRunMutation,
  useSuggestCbtTopicsMutation,
  useCheckCbtRunMutation,
  useSubmitCbtRunMutation,
  useSendBackCbtRunMutation,
  useApproveCbtRunMutation,
  useRetryCbtRunMutation,
  useCancelCbtRunMutation,
  useEditCbtDraftMutation,
  useDeleteCbtDraftMutation,
  useRestoreCbtDraftMutation,
  useConfirmCbtDraftMutation,
  useRecheckCbtDraftMutation,
  useRewriteCbtDraftMutation,
  useGetAllKybVerificationsQuery,
  useGetKybVerificationByIdQuery,
  useApproveKybVerificationMutation,
  useRejectKybVerificationMutation,
  useUpdateKybDocStatusMutation,
  useGetPartnersQuery,
  useGetPartnerByIdQuery,
  useVerifyPartnerMutation,
  useSuspendPartnerMutation,
  useReinstatePartnerMutation,
  useApprovePartnerNameMutation,
  useRejectPartnerNameMutation,
  useGetAllContentCreatorsQuery,
  useRegisterCreatorMutation,
  useGetAllPendingJobsQuery,
  useApproveJobMutation,
  useRejectJobMutation,
  useEditJobMutation,
} = apiSlice;
